import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ROLE_HOME } from "../../components/ProtectedRoute";

/**
 * Public landing page for the Elyon HMS embed integration. Reached via the
 * login_url returned by POST /api/auth/integrations/sso-url/ (server-to-server,
 * called from Elyon HMS's own backend) — exchanges the one-time code in the
 * URL for a real session, then redirects straight into the staff app. The
 * staff member never sees this page render a login form; if the code is
 * invalid/expired it just falls back to the normal /login screen.
 */
export default function SsoLandingPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { loginWithSsoCode } = useAuth();
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        const user = await loginWithSsoCode(code);
        const params = new URLSearchParams(window.location.search);
        const wantsCompact = params.get("compact") === "1";
        const home = (wantsCompact && user.role === "staff")
          ? "/staff/compact"
          : (ROLE_HOME[user.role] || "/login");
        navigate(home, { replace: true });
      } catch (err) {
        setError(
          err.response?.data?.error || "This link is invalid or has expired."
        );
      }
    })();
  }, [code, loginWithSsoCode, navigate]);

  if (error) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          <p style={styles.message}>{error}</p>
          <button style={styles.button} onClick={() => navigate("/login", { replace: true })}>
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <span style={styles.spinner} />
        <p style={styles.message}>Signing you in…</p>
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0B1530",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  spinner: {
    width: "28px",
    height: "28px",
    border: "3px solid rgba(255,255,255,0.2)",
    borderTopColor: "#408DC9",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin 0.7s linear infinite",
  },
  message: {
    color: "#F1F5F9",
    fontSize: "14px",
  },
  button: {
    background: "linear-gradient(135deg, #408DC9, #3478AD)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
};
